import * as R from 'ramda'

const mapPageQuestion = (pageQuestion, store) => ({
    id: pageQuestion.id,
    parentId: pageQuestion.parentId,
    title: pageQuestion.questionTitle(store),
    skipLogic: pageQuestion.skipLogic,
})

const getPagesAndQuestions = (survey, store) => (
    survey.pages.map((page) => ({
        id: page.id,
        title: page.title,
        pageType: page.pageType,
        questions: R.pathOr([], ['questions'], page).map((pageQuestion) => mapPageQuestion(pageQuestion, store)),
    }))
)

const getInvalidInheritance = (pageQuestions, pageQuestionIndicies, pageQuestionPageIndicies) => (
    pageQuestions
        .filter(({ parentId }) => !R.isNil(parentId))
        .filter(({ parentId, id }) => (
            !R.has(parentId, pageQuestionIndicies)
            || pageQuestionIndicies[parentId] >= pageQuestionIndicies[id]
            || pageQuestionPageIndicies[parentId] >= pageQuestionPageIndicies[id]
        ))
)

const getInvalidPageToPageLogic = (skipLogic, pageIndices) => (
    skipLogic
        .filter((logic) => (
            logic.action.kind === 'PageToPage'
            && R.pathOr('', ['trigger', 'condition', 'kind'], logic) === 'CurrentPage'
            && (
                !R.hasPath([logic.action.pageId], pageIndices)
                || !R.hasPath([logic.trigger.condition.pageId], pageIndices)
                || pageIndices[logic.action.pageId] < pageIndices[logic.trigger.condition.pageId]
            )
        ))
        .map((logic) => logic.trigger.condition.pageId)
)

const hasInvalidPageQuestionTrigger = (logic, targetIndex, indicies) => (
    R.pathOr([], ['triggerGroups'], logic).some((tg) => (
        R.pathOr([], ['children'], tg).some((child) => (
            R.pathOr([], ['triggers'], child).some((trigger) => (
                (
                    R.hasPath(['condition', 'pageQuestionId'], trigger)
                    && (
                        !R.has(trigger.condition.pageQuestionId, indicies)
                        || indicies[trigger.condition.pageQuestionId] >= targetIndex
                    )
                )
            ))
        ))
    ))
)

const hasInvalidPageTrigger = (logic, targetIndex, indicies) => (
    R.pathOr([], ['triggerGroups'], logic).some((tg) => (
        R.pathOr([], ['children'], tg).some((child) => (
            R.pathOr([], ['triggers'], child).some((trigger) => (
                (
                    R.pathOr('', ['condition', 'kind'], trigger) === 'CurrentPage'
                    && (
                        !R.has(trigger.condition.pageId, indicies)
                        || indicies[trigger.condition.pageId] > targetIndex
                    )
                )
            ))
        ))
    ))
)

const getInvalidSkipToPageLogic = (skipLogic, pageIndices, pageQuestionPageIndicies) => (
    skipLogic
        .filter((logic) => (
            logic.action.kind === 'SkipToPage'
            && (
                !R.hasPath([logic.action.pageId], pageIndices)
                || hasInvalidPageTrigger(logic, pageIndices[logic.action.pageId], pageIndices)
                || hasInvalidPageQuestionTrigger(
                    logic,
                    pageIndices[logic.action.pageId],
                    pageQuestionPageIndicies,
                )
            )
        ))
        .map((logic) => logic.action.pageId)
)

const getInvalidShowPageLogic = (skipLogic, pageIndices, pageQuestionPageIndicies) => (
    skipLogic
        .filter((logic) => (
            logic.action.kind === 'ShowPage'
            && (
                !R.hasPath([logic.action.pageId], pageIndices)
                || hasInvalidPageQuestionTrigger(
                    logic,
                    pageIndices[logic.action.pageId],
                    pageQuestionPageIndicies,
                )
            )
        ))
        .map((logic) => logic.action.pageId)
)

const getInvalidShowOptionLogic = (skipLogic, pageQuestionIndicies) => (
    skipLogic
        .filter((logic) => (
            logic.action.kind === 'ShowQuestionOption'
            && (
                !R.hasPath([logic.action.pageQuestionId], pageQuestionIndicies)
                || hasInvalidPageQuestionTrigger(
                    logic,
                    pageQuestionIndicies[logic.action.pageQuestionId],
                    pageQuestionIndicies,
                )
            )
        ))
        .map((logic) => logic.action.pageQuestionId)
)

const getInvalidShowQuestionLogic = (skipLogic, pageQuestionIndicies) => (
    skipLogic
        .filter((logic) => (
            logic.action.kind === 'ShowQuestion'
            && (
                !R.hasPath([logic.action.pageQuestionId], pageQuestionIndicies)
                || hasInvalidPageQuestionTrigger(
                    logic,
                    pageQuestionIndicies[logic.action.pageQuestionId],
                    pageQuestionIndicies,
                )
            )
        ))
        .map((logic) => logic.action.pageQuestionId)
)

const getTitles = (pagesAndQuestions) => {
    const pageQuestionTitles = pagesAndQuestions
        .map(({ questions }) => (
            questions.map(({ id, title }) => [id, title])
        ))
        .flat()
        .reduce((map, [id, title]) => (
            Object.assign(map, {
                [id]: title,
            })
        ), {})

    const pageTitles = pagesAndQuestions
        .reduce((map, { id, title }) => (
            Object.assign(map, {
                [id]: title,
            })
        ), {})

    return {
        pageQuestionTitles,
        pageTitles,
    }
}

const getIndicies = (pagesAndQuestions) => {
    const pageIndices = pagesAndQuestions.reduce((map, { id }, index) => (
        Object.assign(map, { [id]: index })
    ), {})

    const pageQuestionPageIndicies = pagesAndQuestions
        .map(({ questions }, index) => (
            questions.map(({ id }) => [id, index])
        ))
        .flat()
        .reduce((map, [id, index]) => (
            Object.assign(map, { [id]: index })
        ), {})

    const pageQuestions = pagesAndQuestions
        .map(({ questions }) => questions)
        .flat()

    const pageQuestionIndicies = pageQuestions
        .reduce((map, { id }, index) => (
            Object.assign(map, { [id]: index })
        ), {})

    return {
        pageIndices,
        pageQuestionPageIndicies,
        pageQuestionIndicies,
    }
}

const getWarnings = (pagesAndQuestions, skipLogic, { pageQuestionTitles, pageTitles }) => {
    const {
        pageIndices,
        pageQuestionPageIndicies,
        pageQuestionIndicies,
    } = getIndicies(pagesAndQuestions)

    const pageQuestions = pagesAndQuestions
        .map(({ questions }) => questions)
        .flat()

    const invalidInheritance = getInvalidInheritance(
        pageQuestions,
        pageQuestionIndicies,
        pageQuestionPageIndicies,
    ).map(({ id, parentId }) => ({ id, parentId }))

    const invalidPageToPageLogic = getInvalidPageToPageLogic(skipLogic, pageIndices)
    const invalidSkipToPageLogic = getInvalidSkipToPageLogic(
        skipLogic,
        pageIndices,
        pageQuestionPageIndicies,
    )
    const invalidShowPageLogic = getInvalidShowPageLogic(
        skipLogic,
        pageIndices,
        pageQuestionPageIndicies,
    )

    const invalidShowQuestionLogic = getInvalidShowQuestionLogic(skipLogic, pageQuestionIndicies)
    const invalidShowOptionLogic = pageQuestions.flatMap(({ skipLogic }) => (
        !skipLogic
            ? []
            : getInvalidShowOptionLogic(skipLogic, pageQuestionIndicies)
    ))

    console.log('invalidInheritance invalidInheritance', invalidInheritance)
    console.log('invalidInheritance invalidPageToPageLogic', invalidPageToPageLogic)
    console.log('invalidInheritance invalidSkipToPageLogic', invalidSkipToPageLogic)
    console.log('invalidInheritance invalidShowPageLogic', invalidShowPageLogic)
    console.log('invalidInheritance invalidShowQuestionLogic', invalidShowQuestionLogic)
    console.log('invalidInheritance invalidShowOptionLogic', invalidShowOptionLogic)
    console.log('invalidInheritance pageTitles', pageTitles)
    console.log('invalidInheritance pageQuestionTitles', pageQuestionTitles)
    const isSkipLogicAffected = (
        invalidPageToPageLogic.length
        || invalidSkipToPageLogic.length
        || invalidShowPageLogic.length
        || invalidShowQuestionLogic.length
        || invalidShowOptionLogic.length
    )

    return {
        isSkipLogicAffected: Boolean(isSkipLogicAffected),
        isInheritanceAffected: Boolean(invalidInheritance.length),
        invalidPageToPageLogic: invalidPageToPageLogic.map((pageId) => pageTitles[pageId]),
        invalidSkipToPageLogic: invalidSkipToPageLogic.map((pageId) => pageTitles[pageId]),
        invalidShowPageLogic: invalidShowPageLogic.map((pageId) => pageTitles[pageId]),
        invalidShowQuestionLogic: invalidShowQuestionLogic.map((id) => pageQuestionTitles[id]),
        invalidShowOptionLogic: invalidShowOptionLogic.map((id) => pageQuestionTitles[id]),
        invalidInheritance: invalidInheritance.map((invalid) => ({ child: pageQuestionTitles[invalid.id], parent: pageQuestionTitles[invalid.parentId]})),
    }
}

const warningsAfterPageMove = (survey, store) => {
    const { skipLogic } = survey

    const allPagesAndQuestions = getPagesAndQuestions(survey, store)

    return getWarnings(
        allPagesAndQuestions,
        skipLogic,
        getTitles(allPagesAndQuestions),
    )
}

const warningsAfterPageRemove = (survey, page, store) => {
    const { skipLogic } = survey

    const allPagesAndQuestions = getPagesAndQuestions(survey, store)

    const pagesAndQuestions = allPagesAndQuestions.filter(({ id }) => id !== page.id)

    return getWarnings(
        pagesAndQuestions,
        skipLogic,
        getTitles(allPagesAndQuestions),
    )
}

const warningsAfterPageQuestionRemove = (survey, question, store) => {
    const { skipLogic } = survey

    const allPagesAndQuestions = getPagesAndQuestions(survey, store)
    const allPagesAndQuestionsTitles = getTitles(allPagesAndQuestions)

    const pagesAndQuestions = allPagesAndQuestions.map((page) => {
        page.questions = page.questions.slice().filter(({ id }) => id !== question.id)
        return page
    })

    return getWarnings(
        pagesAndQuestions,
        skipLogic,
        allPagesAndQuestionsTitles,
    )
}

const affectedSkipLogicAfterPageQuestionMove = (survey, pendingMove, store) => {
    const {
        to,
        destinationIndex,
        question,
    } = pendingMove

    const { skipLogic } = survey

    const allPagesAndQuestions = getPagesAndQuestions(survey, store)

    const reorderredPagesAndQuestions = allPagesAndQuestions
        .map((page) => {
            if (page.id === to.getPageId()) {
                page.questions.splice(destinationIndex, 0, mapPageQuestion(question, store))
            }
            return page
        })

    return getWarnings(
        reorderredPagesAndQuestions,
        skipLogic,
        getTitles(reorderredPagesAndQuestions),
    )
}

export {
    affectedSkipLogicAfterPageQuestionMove,
    warningsAfterPageQuestionRemove,
    warningsAfterPageRemove,
    warningsAfterPageMove,
}
